/**
 * BatchInvitationModal Component - Send Multiple Invitations
 *
 * Modal with custom header, shared InvitationInput, invitation list,
 * empty state, and dynamic footer with Polish grammar support.
 *
 * @module components/workspace/BatchInvitationModal
 */

import { memo, useState, useCallback } from 'react';
import { Button, Modal, Text } from '@mantine/core';
import { IconUsers, IconX, IconMail, IconSend } from '@tabler/icons-react';
import { Trans, useTranslation } from 'react-i18next';

import {
  InvitationInput,
  InvitationListItem,
} from '@/components/workspace/shared';
import { EmptyState } from '@/components/common/EmptyState';
import { FormFooter } from '@/components/common/form';
import { AppScrollArea } from '@/components/ui/AppScrollArea';
import { NotificationService } from '@/services/notificationService';
import { useDeviceLayout } from '@/hooks';
import { INVITATION_BATCH_MAX_SIZE } from '@/constants/workspace';
import {
  INVITE_MODAL_HEADER_ICON_INNER_SIZE,
  INVITE_MODAL_CLOSE_ICON_SIZE,
  INVITE_MODAL_MAX_WIDTH,
  INVITE_MODAL_SEND_ICON_SIZE,
  INVITE_MODAL_LIST_MAX_HEIGHT,
  ICON_SIZE_EMPTY_STATE,
} from '@/constants/ui';
import type { PendingInvitation, WorkspaceRole } from '@/types/workspace';
import type { InvitationRequestDto } from '@/types/api/requests';
import type { FailedInvitationResponse } from '@/types/api/responses';
import styles from './BatchInvitationModal.module.css';

interface BatchInvitationModalProps {
  opened: boolean;
  onClose: () => void;
  onSend: (invitations: InvitationRequestDto[]) => Promise<{
    success: boolean;
    successCount: number;
    failureCount: number;
    failedInvitations?: FailedInvitationResponse[];
    error?: string;
  }>;
  isSending?: boolean;
  workspaceName?: string;
}

interface ModalHeaderProps {
  workspaceName?: string;
  onClose: () => void;
}

interface InvitationListSectionProps {
  entries: PendingInvitation[];
  onRemove: (id: string) => void;
  onRoleChange: (id: string, newRole: WorkspaceRole) => void;
  isSending: boolean;
}

interface ModalFooterProps {
  entryCount: number;
  isSending: boolean;
  onCancel: () => void;
  onSend: () => void;
}

function ModalHeader({ workspaceName, onClose }: ModalHeaderProps) {
  const { t } = useTranslation();

  return (
    <div className={styles.header}>
      <div className={styles.headerIcon}>
        <IconUsers size={INVITE_MODAL_HEADER_ICON_INNER_SIZE} />
      </div>
      <div className={styles.headerText}>
        <Text className={styles.title}>
          {t('workspace.invitations.modalTitle')}
        </Text>
        <Text className={styles.subtitle}>
          <Trans
            i18nKey="workspace.invitations.modalSubtitle"
            values={{ workspace: workspaceName }}
            components={{ strong: <strong /> }}
          />
        </Text>
      </div>
      <button
        type="button"
        className={styles.closeButton}
        onClick={onClose}
        aria-label={t('common.close')}
      >
        <IconX size={INVITE_MODAL_CLOSE_ICON_SIZE} />
      </button>
    </div>
  );
}

function InvitationListSection({
  entries,
  onRemove,
  onRoleChange,
  isSending,
}: InvitationListSectionProps) {
  const { t } = useTranslation();

  return (
    <div className={styles.listSection}>
      <div className={styles.listHeader}>
        <Text className={styles.listLabel}>
          {t('workspace.invitations.listTitle')}
        </Text>
        <Text className={styles.listCounter}>
          {entries.length}/{INVITATION_BATCH_MAX_SIZE}
        </Text>
      </div>
      {entries.length > 0 ? (
        <AppScrollArea maxHeight={INVITE_MODAL_LIST_MAX_HEIGHT}>
          <div className={styles.listContainer}>
            {entries.map((invitation) => (
              <InvitationListItem
                key={invitation.id}
                invitation={invitation}
                onRemove={onRemove}
                onRoleChange={onRoleChange}
                disabled={isSending}
              />
            ))}
          </div>
        </AppScrollArea>
      ) : (
        <EmptyState
          title={t('workspace.invitations.emptyTitle')}
          description={t('workspace.invitations.emptyStateText')}
          icon={<IconMail size={ICON_SIZE_EMPTY_STATE} />}
          size="md"
        />
      )}
    </div>
  );
}

function ModalFooter({
  entryCount,
  isSending,
  onCancel,
  onSend,
}: ModalFooterProps) {
  const { t } = useTranslation();
  const isDisabled = entryCount === 0 || isSending;

  return (
    <FormFooter
      className={styles.footer}
      leftActions={
        <Button variant="subtle" onClick={onCancel} disabled={isSending}>
          {t('common.cancel')}
        </Button>
      }
      rightActions={
        <Button
          onClick={onSend}
          disabled={isDisabled}
          loading={isSending}
          leftSection={<IconSend size={INVITE_MODAL_SEND_ICON_SIZE} />}
        >
          {t('workspace.invitations.send', { count: entryCount })}
        </Button>
      }
    />
  );
}

export const BatchInvitationModal = memo(function BatchInvitationModal({
  opened,
  onClose,
  onSend,
  isSending = false,
  workspaceName,
}: BatchInvitationModalProps) {
  const { t } = useTranslation();
  const { isMobile } = useDeviceLayout();

  const [entries, setEntries] = useState<PendingInvitation[]>([]);

  const handleAdd = useCallback((invitation: Omit<PendingInvitation, 'id'>) => {
    const newEntry: PendingInvitation = {
      id: crypto.randomUUID(),
      email: invitation.email,
      role: invitation.role,
    };
    setEntries((previous) => [...previous, newEntry]);
  }, []);

  const handleRemove = useCallback((id: string) => {
    setEntries((previous) =>
      previous.filter((invitation) => invitation.id !== id)
    );
  }, []);

  const handleRoleChange = useCallback((id: string, newRole: WorkspaceRole) => {
    setEntries((previous) =>
      previous.map((invitation) =>
        invitation.id === id ? { ...invitation, role: newRole } : invitation
      )
    );
  }, []);

  const formatFailureDetails = useCallback(
    (failedInvitations: FailedInvitationResponse[]): string => {
      return failedInvitations
        .map((item) => {
          const reasonKey = `workspace.invitations.failReasons.${item.reason}`;
          const translated = t(reasonKey, { defaultValue: '' });
          const reason = translated || item.reason;
          return `- ${item.email}: ${reason}`;
        })
        .join('\n');
    },
    [t]
  );

  const handleSend = useCallback(async () => {
    if (entries.length === 0) {
      return;
    }

    const invitations: InvitationRequestDto[] = entries.map((invitation) => ({
      email: invitation.email,
      role: invitation.role,
    }));

    try {
      const result = await onSend(invitations);

      if (result.success) {
        setEntries([]);
        onClose();
      } else if (result.successCount === 0) {
        const details = result.failedInvitations
          ? formatFailureDetails(result.failedInvitations)
          : '';
        NotificationService.showError(
          details
            ? t('workspace.invitations.sendAllFailedDetails', {
                failed: result.failureCount,
                details,
              })
            : t('workspace.invitations.sendError')
        );
      } else {
        const details = result.failedInvitations
          ? formatFailureDetails(result.failedInvitations)
          : '';
        NotificationService.showWarning(
          details
            ? t('workspace.invitations.sendPartialDetails', {
                success: result.successCount,
                failed: result.failureCount,
                details,
              })
            : t('workspace.invitations.sendPartial', {
                success: result.successCount,
                failed: result.failureCount,
              })
        );
        setEntries([]);
        onClose();
      }
    } catch {
      NotificationService.showError(t('workspace.invitations.sendError'));
    }
  }, [entries, onSend, onClose, t, formatFailureDetails]);

  const handleClose = useCallback(() => {
    setEntries([]);
    onClose();
  }, [onClose]);

  const isAtLimit = entries.length >= INVITATION_BATCH_MAX_SIZE;

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      withCloseButton={false}
      size={INVITE_MODAL_MAX_WIDTH}
      classNames={{ body: styles.modalBody }}
      radius="lg"
      centered
      fullScreen={isMobile}
    >
      <ModalHeader workspaceName={workspaceName} onClose={handleClose} />
      <div className={styles.body}>
        <InvitationInput
          onAdd={handleAdd}
          pendingInvitations={entries}
          isSubmitting={isSending}
          isAtLimit={isAtLimit}
          showMailIcon
        />
        <InvitationListSection
          entries={entries}
          onRemove={handleRemove}
          onRoleChange={handleRoleChange}
          isSending={isSending}
        />
      </div>
      <ModalFooter
        entryCount={entries.length}
        isSending={isSending}
        onCancel={handleClose}
        onSend={handleSend}
      />
    </Modal>
  );
});
